import { DecimalPipe, NgTemplateOutlet } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, inject, input, signal, viewChild } from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { ENVIRONMENT, SharedService, UserService } from '@cfp/core';
import { SubsidyBuildingBusinessCaseInfo } from '@cfp/shared/building/subsidy-business-case-info';
import { BusinessCaseEnergyUsageReductionInfoListRow } from '@cfp/shared/ui/business-case-energy-usage-reduction-info-list-row';
import {
  MeasurementArea,
  MeasurementCostPerSquare,
  MeasurementMassPerSquare,
  MeasurementType
} from '@cfp/shared/ui/measurement';
import {
  ButtonToggle,
  Card,
  Co2Module,
  CurrencyModule,
  InfoList,
  InfoListItem,
  InfoModalButton,
  LiveRegionStatusUpdaterModule,
  LoadingSpinner,
  PaybackPeriodModule
} from '@cfp/ui';
import { TranslateModule } from '@ngx-translate/core';
import { BuildingBusinessCaseDTO, BuildingDetailsDTO, BuildingsApi } from 'csr-asset-app';

@Component({
  selector: 'cfp-building-business-case',
  templateUrl: './building-business-case-card.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    TranslateModule,
    Card,
    InfoModalButton,
    ButtonToggle,
    InfoList,
    InfoListItem,
    CurrencyModule,
    LiveRegionStatusUpdaterModule,
    PaybackPeriodModule,
    LoadingSpinner,
    Co2Module,
    DecimalPipe,
    BusinessCaseEnergyUsageReductionInfoListRow,
    MeasurementMassPerSquare,
    MeasurementCostPerSquare,
    MeasurementArea,
    SubsidyBuildingBusinessCaseInfo,
    NgTemplateOutlet
  ]
})
export class BuildingBusinessCaseCard {
  public readonly portfolioId = input.required<number>();
  public readonly buildingId = input.required<number>();
  public readonly building = input.required<BuildingDetailsDTO>();
  public readonly showEnergyUsage = input<boolean>(true);
  public readonly businessCaseFromInput = input<BuildingBusinessCaseDTO>(undefined, { alias: 'businessCase' });
  public readonly useH1 = input<boolean>(false);

  protected businessCaseEnergyUsageReductionInfoListRowComponent = viewChild(
    BusinessCaseEnergyUsageReductionInfoListRow
  );

  protected readonly MeasurementType = MeasurementType;
  protected readonly showM2 = signal<boolean>(false);

  protected readonly environment = inject(ENVIRONMENT);
  private readonly buildingsApi = inject(BuildingsApi);
  private readonly sharedService = inject(SharedService);
  private readonly userService = inject(UserService);

  protected readonly hasSubsidyLicense = toSignal(this.userService.hasLicense('subsidy'));

  private readonly businessCaseResource = this.buildingsApi.v2BuildingsIdBusinesscaseGet((() =>
    this.businessCaseFromInput() ? undefined : this.buildingId()) as () => number);

  protected readonly isLoading = computed(() => !this.businessCaseFromInput() && this.businessCaseResource.isLoading());
  protected readonly hasError = computed(() => !this.businessCaseFromInput() && !!this.businessCaseResource.error());
  protected readonly businessCase = computed(() => this.businessCaseFromInput() ?? this.businessCaseResource.value());

  constructor() {
    this.sharedService.reloadComponentObservable.pipe(takeUntilDestroyed()).subscribe(() => {
      this.businessCaseResource.reload();
    });
  }

  protected toggleM2(): void {
    this.showM2.update((showM2) => !showM2);
  }
}
